import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from 'react';

import { Container, Content } from 'styles/sgo_wrappers';
import { useHistory, useLocation } from 'react-router-dom';

import { useAuth } from 'hooks/auth';
import { useWindow } from 'hooks/window';
import { useChanges } from 'hooks/changes';

import SGOHeader from 'components/SGOHeader';
import SGONavbar from 'components/SGONavbar';
import ScrollTop from 'components/ScrollTop';
import SGOFooter from 'components/SGOFooter';
import Loading from 'components/Loading';
import IncludeButton from 'components/IncludeButton';
import * as yup from 'yup';

import api from 'services/api';

import { useCredentials } from 'hooks/credentials';

import { formatNumber } from 'utils/calcTotal';
import {
  endOfDay,
  formatDate,
  handleTimeZone,
  minimumAllowedDate,
} from 'utils/formatDate';

import { IncludeProps } from 'styles/dialog_include';
import * as I from 'styles/dialog_include';
import { FaDollarSign, FaPlusCircle } from 'react-icons/fa';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import InputDialog from 'components/InputDialog';
import getValidationErrors from 'utils/getValidationErrors';
import { useSpring } from 'react-spring';
import { subDays, addDays } from 'date-fns';
import {
  FinHeader,
  FinContent,
  FinItem,
  FinContentHeader,
  Data,
  Anterior,
  Parcela,
  Saldo,
  Wrapper,
  InfoContainer,
  IncButton,
} from './styles';

interface StateProps {
  finHeader: {
    num: number;
    cod: string;
    desc: string;
    valor: number;
    date: string;
  };
}

interface ListProps {
  seq: number;
  dtmov: string;
  ant: number;
  parc: number;
  saldo: number;
}

interface FormData {
  num: string;
  cod: string;
  date: string;
  value: string;
}

const FinGDDetail: React.FC = () => {
  const { user } = useAuth();
  const { width } = useWindow();
  const formRef = useRef<FormHandles>(null);
  const location = useLocation<StateProps>();
  const history = useHistory();
  const { errorHandling, handlePermission } = useCredentials();
  const [loading, setLoading] = useState(false);
  const [finHeader] = useState(() => {
    return location.state.finHeader || ({} as StateProps);
  });
  const { currDate } = useChanges();

  const [prevDate, setPrevDate] = useState('');
  const [today] = useState(
    `${new Date().getFullYear()}-${(new Date().getMonth() + 1)
      .toString()
      .padStart(2, '0')}-${new Date().getDate().toString().padStart(2, '0')}`,
  );

  const [inc, setInc] = useState<IncludeProps>(() => {
    return {
      title: 'Inclusão',
      open: false,
      content: '',
    };
  });

  const [list, setList] = useState<ListProps[]>([]);

  const getList = useCallback(async () => {
    setLoading(true);
    try {
      const data = {
        num: finHeader.num,
        cod: finHeader.cod,
      };
      const response = await api.get(
        `/sgo/fin_gd_detail.php?data=${JSON.stringify(data)}`,
      );
      setList(response.data);
      setPrevDate(response.data[response.data.length - 1].dtmov);
    } catch (err) {
      errorHandling(err);
    }
    setLoading(false);
  }, [errorHandling, finHeader.cod, finHeader.num]);

  useEffect(() => {
    handlePermission(['NAC', 'ZON'], true);
    if (!location.state.finHeader) {
      history.replace(location.pathname.replace('/detail', ''));
    }

    getList();
  }, [getList, handlePermission, history, location.pathname, location.state]);

  const totalPago = useMemo(() => {
    return list.reduce((accum, { parc }: ListProps) => {
      return accum + parc;
    }, 0);
  }, [list]);

  const handleFinish = useCallback(
    async (data: FormData) => {
      try {
        const { month } = currDate;
        let parsedMinDate;
        if ([1, 7].indexOf(month) > -1) {
          parsedMinDate = minimumAllowedDate(prevDate, true);
        } else {
          parsedMinDate = minimumAllowedDate(prevDate);
        }
        // const parsedMinDate = new Date(parseISO(prevDate));

        if (
          parsedMinDate.getUTCDate() === handleTimeZone(prevDate).getUTCDate()
        ) {
          parsedMinDate = addDays(parsedMinDate, 1);
        }

        const parsedMaxDate = endOfDay(new Date());

        setLoading(true);
        formRef.current?.setErrors({});
        const schema = yup.object().shape({
          date: yup
            .date()
            .typeError('Data inválida')
            .min(
              parsedMinDate,
              `O lançamento deve ser posterior a ${formatDate(
                subDays(parsedMinDate, 1).toISOString(),
              )}`,
            )
            .max(
              parsedMaxDate,
              `Data limite excedida (${formatDate(
                new Date(parsedMaxDate).toISOString(),
              )})`,
            ),
          value: yup
            .number()
            .min(0.01, 'O valor informado deve ser superior a zero ( R$ 0,00 )')
            .max(
              finHeader.valor + 0.01 - totalPago,
              `O valor informado é superior ao valor restante ( R$ ${formatNumber(
                finHeader.valor - totalPago,
              )} )`,
            ),
        });

        await schema.validate(data, {
          abortEarly: false,
        });

        setInc((state) => ({ ...state, open: false }));

        const newData = {
          ...data,
          ant: (finHeader.valor - totalPago).toFixed(2),
          saldo: (
            finHeader.valor -
            (totalPago + parseFloat(data.value))
          ).toFixed(2),
        };

        const send = new FormData();
        send.append('data', JSON.stringify(newData));

        await api.post('/sgo/fin_gd_insert.php', send, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        setLoading(false);
        getList();
      } catch (err) {
        setLoading(false);
        if (err instanceof yup.ValidationError) {
          const errors = getValidationErrors(err);

          formRef.current?.setErrors(errors);
          return;
        }
        errorHandling(err);
      }
    },
    [currDate, errorHandling, finHeader.valor, getList, prevDate, totalPago],
  );

  const buttonStyle = useSpring({
    opacity: width <= 889 ? 1 : 0,
    marginTop: width <= 889 ? '15px' : '0px',
    marginBottom: width <= 889 ? '15px' : '0px',
    pointerEvents: width <= 889 ? 'all' : 'none',
  });

  return (
    <Container>
      <Loading isLoading={loading} />

      {list.length > 0 &&
        list[list.length - 1].saldo > 0 &&
        user.perfil === 'ZON' &&
        user.zoncod !== finHeader.cod &&
        width >= 890 && (
          <IncludeButton isButton>
            <button
              type="button"
              onClick={() => setInc((state) => ({ ...state, open: true }))}
            >
              <FaPlusCircle />
            </button>
          </IncludeButton>
        )}

      <ScrollTop />
      <SGOHeader />
      <SGONavbar
        noLinks
        title={`Acompanhamento Financeiro GD ${finHeader.num}`}
        isResponsible
      />
      <Content>
        <FinHeader>
          <span>
            <p>
              Coord:&nbsp;<strong>{finHeader.desc}</strong>
            </p>
          </span>
          <span>
            <p>
              Valor:&nbsp;<strong>R$ {formatNumber(finHeader.valor)}</strong>
            </p>
          </span>
          <span>
            <p>
              Data:&nbsp;
              <strong>{formatDate(finHeader.date)}</strong>
            </p>
          </span>
        </FinHeader>

        {list.length > 0 &&
          list[list.length - 1].saldo > 0 &&
          user.perfil === 'ZON' &&
          user.zoncod !== finHeader.cod &&
          width < 890 && (
            <IncButton as="button"
              type="button"
              style={buttonStyle}
              onClick={() => setInc((state) => ({ ...state, open: true }))}
            >
              Pagamento da GD
            </IncButton>
          )}
        <FinContent>
          <Wrapper>
            <FinContentHeader>
              <Data>
                <p>Data</p>
              </Data>
              <Anterior>
                <p>S{width >= 420 ? 'aldo' : '.'} Anterior</p>
              </Anterior>
              <Parcela>
                <p>Parcela</p>
              </Parcela>
              <Saldo>
                <p>Saldo</p>
              </Saldo>
            </FinContentHeader>

            {list.map((item, index) => (
              <FinItem $paint={index} key={item.seq}>
                <Data>
                  <p>{formatDate(item.dtmov)}</p>
                </Data>
                <Anterior>
                  <p>R$ {formatNumber(item.ant)}</p>
                </Anterior>
                <Parcela>
                  <p>R$ {formatNumber(item.parc)}</p>
                </Parcela>
                <Saldo>
                  <p>R$ {formatNumber(item.saldo)}</p>
                </Saldo>
              </FinItem>
            ))}
          </Wrapper>
        </FinContent>
        <I.Container scroll="paper" maxWidth={false} open={inc.open}>
          <Form translate={undefined}
            ref={formRef}
            onSubmit={handleFinish}
            initialData={{
              num: finHeader.num,
              cod: finHeader.cod,
              date: today,
              // value: (finHeader.valor - totalPago).toFixed(2),
              value: (0).toFixed(2),
            }}
          >
            <I.Title>
              <h2>{inc.title || 'Inclusão'}</h2>
            </I.Title>
            <I.Content>
              <InfoContainer>
                <InputDialog name="num" isHidden />
                <InputDialog name="cod" isHidden />
                <span>
                  <p>
                    Total da GD:&nbsp;
                    <strong>R$ {formatNumber(finHeader.valor)}</strong>
                  </p>
                </span>
                <span>
                  <p>
                    Total Pago:&nbsp;
                    <strong>R$ {formatNumber(totalPago)}</strong>
                  </p>
                </span>
                <span>
                  <p>
                    Valor Pendente:&nbsp;
                    <strong>
                      R$ {formatNumber(finHeader.valor - totalPago)}
                    </strong>
                  </p>
                </span>
                <span>
                  <p>Data do Pagamento:&nbsp;</p>
                  <InputDialog
                    name="date"
                    type="date"
                    placeholder="dd/mm/aaaa"
                  />
                </span>
                <span>
                  <p>Valor:&nbsp;</p>
                  <InputDialog
                    icon={FaDollarSign}
                    type="number"
                    name="value"
                    mask="currency"
                    step=".01"
                    placeholder="0,00"
                  />
                </span>
              </InfoContainer>
            </I.Content>
            <I.Actions>
              <I.Cancel as="button"
                type="button"
                onClick={() =>
                  setInc({
                    ...inc,
                    open: false,
                  })
                }
              >
                Cancelar
              </I.Cancel>
              <I.Confirm as="button" type="submit">Finalizar</I.Confirm>
            </I.Actions>
          </Form>
        </I.Container>
      </Content>
      <SGOFooter />
    </Container>
  );
};

export default FinGDDetail;
